// workspace-scaffold-and-finalize-orchestrator.ts
//
// v3 THIN INIT: workspace KHÔNG git. Scaffold = tạo folder + cài pack tarball +
// symlink + settings + AI setup + GitNexus(install+MCP, KHÔNG analyze — src/ rỗng).
//
// Bỏ HẾT so với v2: git().init(), submodule add src/, cài git hook (post-merge/
// pre-push), maybeCommitWorkspace, maybeCreateWorkspaceRemote. Repo code cài sau
// bằng `avatar add repo` (Phase 05).
import { join } from "node:path";
import { appendAuditEntry } from "../lib/audit-log-appender.js";
import type { CommSetupFlags } from "../lib/comm-setup-non-interactive-flags.js";
import {
  appendOrientationImportToClaudeMd,
  ensureOrientationNoteFromPack,
} from "../lib/ensure-orientation-note-from-pack.js";
import { ensureSecurityScannersInstalled } from "../lib/ensure-security-scanners-installed.js";
import { ensureDir, pathExists } from "../lib/filesystem-helpers.js";
import { installTeamPackWithRetryOnNetworkFail } from "../lib/install-team-pack-with-retry-on-network-fail.js";
import { maybePromptAiOrchestratorAfterAiSetup } from "../lib/maybe-prompt-ai-orchestrator-after-ai-setup.js";
import { mergePackSettingsIntoProjectSettings } from "../lib/merge-pack-settings-into-project-settings.js";
import { isMissingFlagError } from "../lib/non-interactive-context.js";
import {
  createClaudeDirTree,
  writeAvatarWorkspaceMarker,
  writeClaudeGitignore,
  writeProjectKnowledgeFiles,
  writeProjectSettings,
  writeRootClaudeMd,
} from "../lib/project-tree-scaffolder.js";
import { readCliVersion } from "../lib/read-cli-version-from-package-json.js";
import { runAiSetupPhase } from "../lib/run-ai-setup-phase.js";
import { runCommSetupPhase } from "../lib/run-comm-setup-phase.js";
import { runGitnexusSetupPhase } from "../lib/run-gitnexus-setup-phase.js";
import { setupDefaultToolsOnInit } from "../lib/setup-default-tools-on-init.js";
import { syncAllMountDirs } from "../lib/symlink-farm-for-team-pack-mount-dirs.js";
import { TEAM_PACK_RELATIVE_PATH } from "../lib/team-pack-manager.js";
import { log, spinner } from "../lib/terminal-logger.js";
import { logCliResults, updateDependentClis } from "../lib/update-dependent-clis.js";
import { buildScaffoldVariables } from "./init-scaffold-variable-builders.js";
import {
  ensureWorkspaceFolder,
  printInitSuccessBox,
} from "./init-success-rendering-and-helpers.js";

/**
 * Chạy phase Chatops theo đúng tinh thần fail-soft của `init` — VÀ để lỗi thiếu cờ
 * đi xuyên qua.
 *
 * ⛔ Tách khỏi `scaffoldThinWorkspace` là CỐ Ý (lỗ L9): khi khối try/catch nằm lẫn
 * trong hàm dựng workspace dài, cách duy nhất để đo "lỗi thiếu cờ có bị nuốt không"
 * là chạy `avatar init` thật — mà `init` phải login Google và tải pack, nên phép đo
 * đó không bao giờ hermetic. Tách ra thành hàm nhỏ thì đo được bằng unit test.
 */
export async function runCommSetupPhaseFailSoft(args: {
  workspacePath: string;
  commSkip?: boolean;
  commFlags?: CommSetupFlags;
}): Promise<void> {
  if (args.commSkip) {
    log.dim("Bỏ qua Setup Chatops (--comm-skip). Setup sau: avatar chatops-connect");
    return;
  }
  try {
    const commResult = await runCommSetupPhase({
      workspacePath: args.workspacePath,
      flags: args.commFlags,
    });
    if (!commResult.ok && commResult.reason === "deferred") {
      // User chủ động chọn "Setup lại sau" ở retry-loop menu — KHÔNG phải lỗi,
      // không warn "fail". Lỗi thật (throw) mới rơi vào catch bên dưới.
      log.dim("Setup Chatops để sau: avatar chatops-connect");
    }
  } catch (err) {
    // Khối fail-soft này từng nuốt MỌI lỗi ⇒ `avatar init` thoát 0 dù thiếu cờ,
    // máy gọi tưởng đã cấu hình xong. Thiếu cờ KHÔNG phải thứ fail-soft được —
    // nó là lỗi dùng sai lệnh, phải đi tiếp lên và exit ≠0.
    if (isMissingFlagError(err)) throw err;
    log.warn(`  ✗ Setup Chatops fail: ${err instanceof Error ? err.message : err}`);
    log.warn("  Setup lại sau: avatar chatops-connect");
  }
}

// Scaffold workspace v3 (1 flow duy nhất). Tạo folder rỗng + cài pack + môi trường.
export async function scaffoldThinWorkspace(args: {
  workspacePath: string;
  workspaceName: string;
  teamOwner: string;
  description: string;
  packVersion?: string;
  autoYes?: boolean;
  skipTeamPack?: boolean;
  aiSkip?: boolean;
  gitnexusSkip?: boolean;
  commSkip?: boolean;
  /** Cờ comm của T2 — chỉ dùng ở chế độ non-interactive. */
  commFlags?: CommSetupFlags;
}): Promise<void> {
  // 1. Tạo workspace folder + src/ rỗng + notes/ (KHÔNG git init).
  await ensureWorkspaceFolder(args.workspacePath);
  await ensureDir(join(args.workspacePath, "src"));
  await ensureDir(join(args.workspacePath, "notes"));

  // 2. Cài team-ai-pack tarball.
  let pinnedTag = "(skipped)";
  if (!args.skipTeamPack) {
    const sp = spinner("Tải team-ai-pack từ Supabase...");
    sp.stop(); // dừng spinner để menu recovery (nếu cần) không bị đè.
    const result = await installTeamPackWithRetryOnNetworkFail(
      args.workspacePath,
      args.packVersion,
    );
    pinnedTag = result.pinnedTag ?? "(skipped)";
    if (!result.skipped) log.success(`team-ai-pack: ${pinnedTag}`);
  } else {
    log.dim("Skip team-ai-pack (--skip-team-pack).");
  }

  // 3. Scaffold files (CLAUDE.md, settings, knowledge, gitignore).
  const vars = buildScaffoldVariables({
    projectName: args.workspaceName,
    projectDescription: args.description,
    teamOwner: args.teamOwner,
    packVersion: pinnedTag,
  });
  await createClaudeDirTree(args.workspacePath);
  // Marker định danh Avatar workspace — workspace resolver dựa vào file này
  // (đặc thù, không generic) để tránh nhận nhầm project khác làm workspace.
  await writeAvatarWorkspaceMarker(args.workspacePath, {
    avatarVersion: readCliVersion(),
    workspaceName: args.workspaceName,
  });
  await writeProjectKnowledgeFiles(args.workspacePath, vars);
  await writeRootClaudeMd(args.workspacePath, vars);
  await writeProjectSettings(args.workspacePath, vars);
  await writeClaudeGitignore(args.workspacePath);

  // 4. Auto-sync pack content → symlink farm + settings merge + default tools.
  await autoSyncPackOnInit(args.workspacePath, args.autoYes);

  // Ticket #212: seed `notes/orientation.md` từ pack rồi append `@import` vào CLAUDE.md.
  // ⚠️ PHẢI ghi ĐỦ HAI lời gọi — đường `init` KHÔNG thừa hưởng 2 điểm wire của `sync`:
  // autoSyncPackOnInit chỉ chạy syncAllMountDirs + mergePackSettings + setupDefaultTools,
  // KHÔNG gọi thân syncAction.
  // ⚠️ VỊ TRÍ CÓ CHỦ ĐÍCH — sau bước 4, KHÔNG phải cạnh ensureDir(notes) ở bước 1:
  //   (a) pack mới được cài ở bước 2 ⇒ tại bước 1 template chưa tồn tại ⇒ luôn no-op-pack-missing;
  //   (b) CLAUDE.md mới được ghi ở bước 3 bằng backup + ghi đè ⇒ dòng import append trước đó
  //       bị dời sang file `CLAUDE.md.avatar-backup-*`.
  // try/catch fail-soft giống 2 điểm wire bên `sync.ts`: hai hàm dưới TỰ nuốt mọi lỗi thành
  // `status: "failed"` nên hôm nay chúng không reject — nhánh catch này là defense-in-depth cho
  // ngày ai đó thêm đường throw. Ở `init` lớp này đáng giá hơn ở `sync`: ném ở đây làm
  // `scaffoldThinWorkspace` chết GIỮA CHỪNG, để lại workspace dựng dở (pack đã cài, AI setup chưa).
  try {
    const orientation = await ensureOrientationNoteFromPack(args.workspacePath);
    if (orientation.warning) log.warn(`  ! notes/orientation.md: ${orientation.warning}`);
    await appendOrientationImportToClaudeMd(args.workspacePath, orientation.status);
  } catch (err) {
    log.warn(`  ! Seed notes/orientation.md fail: ${err instanceof Error ? err.message : err}.`);
  }

  await appendAuditEntry("init", `workspace=${args.workspaceName}`);

  // 5. AI setup phase.
  let aiResult: Awaited<ReturnType<typeof runAiSetupPhase>> | null = null;
  if (args.aiSkip) {
    log.dim("Bỏ qua AI setup (--ai-skip). Setup sau: avatar ai setup");
  } else {
    aiResult = await runAiSetupPhase({ workspacePath: args.workspacePath });
  }

  // 5b. AI Orchestrator: hỏi enable SAU AI setup, chỉ khi key non-subscription + >1
  //     model (tái dùng count từ aiResult). Fail-mềm — không vỡ init.
  try {
    await maybePromptAiOrchestratorAfterAiSetup(args.workspacePath, aiResult, {
      autoYes: args.autoYes,
    });
  } catch (err) {
    if (isMissingFlagError(err)) throw err;
    log.warn(`  ✗ Setup AI Orchestrator fail: ${err instanceof Error ? err.message : err}`);
  }

  // 5c. Setup Chatops (Mattermost, phase-05) — cần cho /avatar:specs:receive.
  //     Fail-soft: KHÔNG chặn init (đúng pattern AI/GitNexus setup ở trên) — lỗi/skip
  //     → workspace vẫn dùng được, setup lại sau qua `avatar chatops-connect`.
  await runCommSetupPhaseFailSoft(args);

  // 6. GitNexus: install + MCP register THÔI. KHÔNG analyze (src/ rỗng — index per-repo
  //    chạy khi `avatar add repo`). Auto-skip nếu --ai-skip (wiki cần API key từ AI phase).
  let gitnexusResult: Awaited<ReturnType<typeof runGitnexusSetupPhase>> | null = null;
  const skipGitnexus = args.aiSkip || args.gitnexusSkip;
  if (skipGitnexus) {
    log.dim(
      args.gitnexusSkip
        ? "Bỏ qua GitNexus (--gitnexus-skip). Setup sau: avatar gitnexus install"
        : "Bỏ qua GitNexus (auto-skip do --ai-skip).",
    );
  } else {
    gitnexusResult = await runGitnexusSetupPhase({
      workspacePath: args.workspacePath,
      skipAnalyze: true, // v3: init không analyze workspace rỗng.
    });
  }

  // 7. CLI phụ thuộc: check + auto-update (Claude Code, Supabase, git, gitleaks,
  //    trivy). Fail-soft — lỗi không chặn init. init hiếm chạy nên update thẳng OK.
  try {
    log.info("Kiểm tra cập nhật CLI phụ thuộc...");
    const cliResults = await updateDependentClis({ autoUpdate: true });
    logCliResults(cliResults);
  } catch (err) {
    log.warn(`Cập nhật CLI phụ thuộc bỏ qua: ${err instanceof Error ? err.message : err}`);
  }

  await printInitSuccessBox(args.workspacePath, aiResult, gitnexusResult);
}

// Auto-sync pack content vào convention paths Claude Code scan (symlink + settings).
export async function autoSyncPackOnInit(workspacePath: string, autoYes?: boolean): Promise<void> {
  const packDir = join(workspacePath, TEAM_PACK_RELATIVE_PATH);
  if (!(await pathExists(packDir))) {
    log.dim("Pack chưa cài (skip auto-sync). Chạy `avatar sync` sau.");
    return;
  }

  const claudeDir = join(workspacePath, ".claude");
  log.info("Auto-sync pack content vào .claude/ (symlinks + settings)...");

  // Per-operation try/catch — 1 bước fail KHÔNG che giấu các bước khác. Mỗi bước
  // báo lỗi riêng + cuối cùng cảnh báo rõ init CHƯA hoàn tất (thay vì 1 dim warn
  // mơ hồ khiến user tưởng init xong).
  let hadFailure = false;
  try {
    const results = await syncAllMountDirs(packDir, claudeDir, false);
    const created = results.filter((r) => r.action === "created" || r.action === "updated").length;
    const missing = results.filter((r) => r.action === "source-missing").length;
    log.success(
      `  ✓ Symlinks: ${created} created${missing > 0 ? `, ${missing} source-missing` : ""}`,
    );
  } catch (err) {
    hadFailure = true;
    log.warn(`  ✗ Symlink farm fail: ${err instanceof Error ? err.message : err}`);
  }

  try {
    const mergeResult = await mergePackSettingsIntoProjectSettings(workspacePath);
    if (mergeResult.action === "merged") {
      log.success(`  ✓ settings.json merged (${mergeResult.changes.join("; ")})`);
    }
  } catch (err) {
    hadFailure = true;
    log.warn(`  ✗ Merge settings fail: ${err instanceof Error ? err.message : err}`);
  }

  try {
    await setupDefaultToolsOnInit(workspacePath, { autoYes });
  } catch (err) {
    hadFailure = true;
    log.warn(`  ✗ Setup default tools fail: ${err instanceof Error ? err.message : err}`);
  }

  // Security scanner (gitleaks + trivy) cho pull-request-checker — cài qua brew,
  // fail-soft (không brew/lỗi → gate tự skip, KHÔNG fail init).
  try {
    ensureSecurityScannersInstalled();
  } catch (err) {
    log.warn(`  ! Security scanner check fail: ${err instanceof Error ? err.message : err}`);
  }

  if (hadFailure) {
    log.warn("⚠ Auto-sync pack CHƯA hoàn tất — workspace có thể thiếu symlinks/settings.");
    log.warn("  Chạy `avatar sync` để hoàn tất setup.");
  }
}
